import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupsAPI, wishlistAPI } from '../api';
import { useAuth } from '../AuthContext';

function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [group, setGroup] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [showRestrictions, setShowRestrictions] = useState(false);
  const [restrictions, setRestrictions] = useState([]);
  const [showTemplateCustomizer, setShowTemplateCustomizer] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // 'invite', 'restrictions', 'customize'
  const [recipientWishlist, setRecipientWishlist] = useState([]);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [riddleLoading, setRiddleLoading] = useState(false);

  useEffect(() => {
    loadGroupDetails();
    loadAssignment();
    loadRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const loadGroupDetails = async () => {
    try {
      const response = await groupsAPI.getGroup(groupId);
      setGroup(response.data);
      setDescriptionDraft(response.data.description || '');
      setIsEditingDescription(false);
    } catch (err) {
      setError('Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionSave = async () => {
    if (!isOrganizer) return;
    setDescriptionSaving(true);
    setError('');
    try {
      await groupsAPI.updateGroup(groupId, { description: descriptionDraft });
      await loadGroupDetails();
      setIsEditingDescription(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update description');
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleDescriptionCancel = () => {
    setIsEditingDescription(false);
    setDescriptionDraft(group?.description || '');
  };

  const handleGenerateRiddle = async () => {
    setRiddleLoading(true);
    setError('');
    try {
      await groupsAPI.generateRiddle(groupId);
      await loadGroupDetails();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate riddle');
    } finally {
      setRiddleLoading(false);
    }
  };

  const loadAssignment = async () => {
    try {
      const response = await groupsAPI.getMyAssignment(groupId);
      if (response.data.has_assignment) {
        setAssignment(response.data.assignment);
        // Load recipient's wishlist
        loadRecipientWishlist(response.data.assignment.receiver.id);
      }
    } catch (err) {
      // No assignment yet
    }
  };

  const loadRecipientWishlist = async (recipientId) => {
    try {
      const response = await wishlistAPI.getUserWishlist(recipientId);
      setRecipientWishlist(response.data.items);
    } catch (err) {
      // Failed to load wishlist or no permission
      setRecipientWishlist([]);
    }
  };

  const loadRestrictions = async () => {
    try {
      const response = await groupsAPI.getRestrictions(groupId);
      setRestrictions(response.data.restrictions);
    } catch (err) {
      // Error loading restrictions
    }
  };

  const handleCreateAssignments = async () => {
    setAssignmentLoading(true);
    setError('');

    try {
      await groupsAPI.createAssignments(groupId);
      await loadGroupDetails();
      await loadAssignment();
      alert('Secret Santa assignments created! Everyone will receive an email notification.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create assignments');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await groupsAPI.deleteGroup(groupId);
      navigate('/groups');
    } catch (err) {
      setError('Failed to delete group');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await groupsAPI.removeMember(groupId, memberId);
      await loadGroupDetails();
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container">
        <div className="card">
          <div className="error-message">Group not found</div>
        </div>
      </div>
    );
  }

  const isOrganizer = user && group.organizer_id === user.id;

  return (
    <div className="container">
      <button
        className="btn btn-secondary back-button"
        onClick={() => navigate('/groups')}
      >
        Back to Groups
      </button>

      <div className="card">
        <div className="group-detail-header">
          <div className="group-detail-info">
            <h2>{group.name}</h2>
            {group.description && !isEditingDescription && (
              <p className="group-description">{group.description}</p>
            )}
            {isOrganizer && isEditingDescription && (
              <div className="form-group">
                <textarea
                  rows="4"
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  placeholder="Describe your group's budget, deadlines, and other details..."
                />
                <div className="group-detail-edit-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleDescriptionSave}
                    disabled={descriptionSaving}
                  >
                    {descriptionSaving ? 'Saving...' : 'Save Description'}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={handleDescriptionCancel}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          {isOrganizer && (
            <div className="group-detail-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setDescriptionDraft(group.description || '');
                  setIsEditingDescription(true);
                }}
              >
                {isEditingDescription ? 'Editing...' : 'Edit Description'}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteGroup}
              >
                Delete Group
              </button>
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div style={{ marginTop: '1.5rem' }}>
          <p><strong>Organizer:</strong> {group.organizer_name}</p>
          <p><strong>Status:</strong> {group.is_assigned ? 'Assignments Created' : 'Waiting for Assignments'}</p>
          {group.organizer_opts_out && (
            <p style={{ color: '#856404' }}>(Organizer opted out of participation)</p>
          )}
        </div>

        {!isOrganizer && (
          <div
            className="info-message"
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div style={{ flex: 1, minWidth: '200px' }}>
              <strong>Reminder:</strong> Keep your wish list up to date so your Secret Santa knows what to get you.
            </div>
            <button
              className="btn btn-secondary"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => navigate('/wishlist')}
            >
              Update Wish List
            </button>
          </div>
        )}
        {assignment && (
          <div className="assignment-display">
            <h3>You are giving a gift to:</h3>
            <div className="recipient-name">{assignment.receiver.name}</div>
            <p style={{ fontSize: '0.9rem', marginTop: '1rem', opacity: '0.9' }}>
              Remember: Keep it a secret!
            </p>

            {group.riddle && (
              <div className="info-message" style={{ marginTop: '1.5rem', background: '#fff8e1', border: '1px solid #ffd88c' }}>
                <h3 style={{ marginTop: 0 }}>🎭 Secret Santa Riddle</h3>
                <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
                  {group.riddle}
                </p>
              </div>
            )}
            {isOrganizer && group.is_assigned && !group.riddle && group.member_count >= 3 && (
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={handleGenerateRiddle}
                disabled={riddleLoading}
              >
                {riddleLoading ? 'Summoning Riddle...' : 'Generate Secret Riddle'}
              </button>
            )}

            {/* Recipient's Wishlist */}
            {recipientWishlist.length > 0 && (
              <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#c41e3a' }}>
                  🎁 {assignment.receiver.name}'s Wish List
                </h4>
                <div className="wishlist-grid">
                  {recipientWishlist.map((item) => (
                    <div key={item.id} className="wishlist-item-card" style={{ background: 'white' }}>
                      <div className="wishlist-item-content">
                        <h4>{item.name}</h4>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wishlist-link"
                          >
                            🔗 View Link
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recipientWishlist.length === 0 && (
              <p style={{ fontSize: '0.9rem', marginTop: '1rem', color: '#f8f9fa', fontStyle: 'italic' }}>
                ({assignment.receiver.name} hasn't added any items to their wish list yet.)
              </p>
            )}
          </div>
        )}

        {isOrganizer && !group.is_assigned && (
          <div style={{ marginTop: '2rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleCreateAssignments}
              disabled={assignmentLoading || group.member_count < 2}
              style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}
            >
              {assignmentLoading ? 'Contacting Santa...' : '🎅 Get Santa Ready'}
            </button>
            {group.member_count < 2 && (
              <p style={{ color: '#856404', marginTop: '0.5rem', textAlign: 'center' }}>
                Need at least 2 members to create assignments
              </p>
            )}
          </div>
        )}

        {isOrganizer && !group.is_assigned && (
          <div style={{ marginTop: '2rem' }}>
            <div className="tab-navigation">
              <button
                className={`tab-button ${activeTab === 'invite' ? 'active' : ''}`}
                onClick={() => setActiveTab(activeTab === 'invite' ? null : 'invite')}
              >
                📧 Invite People
              </button>
              <button
                className={`tab-button ${activeTab === 'restrictions' ? 'active' : ''}`}
                onClick={() => setActiveTab(activeTab === 'restrictions' ? null : 'restrictions')}
              >
                🚫 Draw Restrictions
              </button>
              <button
                className={`tab-button ${activeTab === 'customize' ? 'active' : ''}`}
                onClick={() => setActiveTab(activeTab === 'customize' ? null : 'customize')}
              >
                🎨 Customize Join Page
              </button>
            </div>

            {activeTab === 'invite' && (
              <div className="tab-content">
                <InviteForm groupId={groupId} inviteCode={group.invite_code} />
              </div>
            )}

            {activeTab === 'restrictions' && (
              <div className="tab-content">
                <RestrictionsManager
                  groupId={groupId}
                  members={group.members}
                  restrictions={restrictions}
                  onRestrictionAdded={loadRestrictions}
                  onRestrictionRemoved={loadRestrictions}
                />
              </div>
            )}

            {activeTab === 'customize' && (
              <div className="tab-content">
                <TemplateCustomizer
                  groupId={groupId}
                  template={group.template}
                  inviteCode={group.invite_code}
                  onTemplateUpdated={loadGroupDetails}
                />
              </div>
            )}

          </div>
        )}

        <div className="member-list">
          <h3>Members ({group.member_count})</h3>
          {group.members && group.members.map((member) => (
            <div key={member.id} className="member-item">
              <div className="member-info">
                <strong>{member.user_name}</strong>
                <small>{member.user_email}</small>
              </div>
              {isOrganizer && !group.is_assigned && member.user_id !== group.organizer_id && (
                <button
                  className="btn btn-danger"
                  style={{ padding: '0.5rem 1rem' }}
                  onClick={() => handleRemoveMember(member.id)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InviteForm({ groupId, inviteCode }) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const inviteLink = `${window.location.origin}/join/${inviteCode}`;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    try {
      const response = await groupsAPI.inviteToGroup(groupId, formData.email, formData.name);
      setMessage(response.data.message);
      setFormData({ email: '', name: '' });
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setMessage('Invite link copied to clipboard!');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '10px' }}>
      <h4 style={{ marginBottom: '1rem' }}>Invite by Email</h4>
      {message && (
        <div className={message.includes('Failed') ? 'error-message' : 'success-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Sending...' : 'Send Invitation'}
        </button>
      </form>

      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #ddd' }}>
        <h4>Or Share Invite Link</h4>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <input
            type="text"
            value={inviteLink}
            readOnly
            style={{ flex: 1, padding: '0.75rem', background: '#fff' }}
          />
          <button className="btn btn-secondary" onClick={copyInviteLink}>
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}

function RestrictionsManager({ groupId, members, restrictions, onRestrictionAdded, onRestrictionRemoved }) {
  const [user1, setUser1] = useState('');
  const [user2, setUser2] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddRestriction = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (!user1 || !user2) {
      setMessage('Please select both users');
      setLoading(false);
      return;
    }

    if (user1 === user2) {
      setMessage('Please select two different users');
      setLoading(false);
      return;
    }

    try {
      await groupsAPI.addRestriction(groupId, parseInt(user1), parseInt(user2));
      setMessage('Restriction added successfully');
      setUser1('');
      setUser2('');
      onRestrictionAdded();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to add restriction');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRestriction = async (restrictionId) => {
    try {
      await groupsAPI.removeRestriction(groupId, restrictionId);
      setMessage('Restriction removed successfully');
      onRestrictionRemoved();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to remove restriction');
    }
  };

  // Restrictions are now unidirectional, so no need to deduplicate
  const uniqueRestrictions = restrictions;

  return (
    <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '10px' }}>
      <h4>Draw Restrictions</h4>
      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
        Prevent specific gift-giving directions. Person 1 will not be assigned to give a gift to Person 2.
        To prevent couples from being matched together, add restrictions in both directions.
      </p>

      {message && (
        <div className={message.includes('Failed') || message.includes('select') ? 'error-message' : 'success-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleAddRestriction}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div className="form-group">
            <label>Person 1</label>
            <select value={user1} onChange={(e) => setUser1(e.target.value)} required>
              <option value="">Select a person</option>
              {members && members.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Person 2</label>
            <select value={user2} onChange={(e) => setUser2(e.target.value)} required>
              <option value="">Select a person</option>
              {members && members.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Adding...' : 'Add Restriction'}
        </button>
      </form>

      {uniqueRestrictions.length > 0 && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #ddd' }}>
          <h4>Current Restrictions</h4>
          <div style={{ marginTop: '1rem' }}>
            {uniqueRestrictions.map((restriction) => (
              <div
                key={restriction.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  background: '#fff',
                  borderRadius: '5px',
                  marginBottom: '0.5rem'
                }}
              >
                <span>
                  <strong>{restriction.user_name}</strong> → <strong>{restriction.restricted_user_name}</strong>
                </span>
                <button
                  className="btn btn-danger"
                  style={{ padding: '0.5rem 1rem' }}
                  onClick={() => handleRemoveRestriction(restriction.id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateCustomizer({ groupId, template, inviteCode, onTemplateUpdated }) {
  const [formData, setFormData] = useState({
    theme: template?.theme || 'default',
    welcome_message: template?.welcome_message || '',
    button_text: template?.button_text || 'Join Group',
    background_color: template?.background_color || '#f8f9fa'
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const themes = [
    { value: 'default', label: 'Default', emoji: '✨' },
    { value: 'festive', label: 'Festive', emoji: '🎄' },
    { value: 'elegant', label: 'Elegant', emoji: '💎' },
    { value: 'fun', label: 'Fun', emoji: '🎉' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    try {
      await groupsAPI.updateGroup(groupId, {
        template: formData
      });
      setMessage('Join page customization saved successfully!');
      onTemplateUpdated();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save customization');
    } finally {
      setLoading(false);
    }
  };

  const previewLink = `${window.location.origin}/join/${inviteCode}`;

  return (
    <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#f8f9fa', borderRadius: '10px' }}>
      <h4>Join Page Customization</h4>
      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
        Customize how your join page looks when people click your invite link.
      </p>

      {message && (
        <div className={message.includes('Failed') ? 'error-message' : 'success-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="theme">Theme</label>
          <select
            id="theme"
            name="theme"
            value={formData.theme}
            onChange={handleChange}
          >
            {themes.map((theme) => (
              <option key={theme.value} value={theme.value}>
                {theme.emoji} {theme.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="welcome_message">Welcome Message (Optional)</label>
          <textarea
            id="welcome_message"
            name="welcome_message"
            value={formData.welcome_message}
            onChange={handleChange}
            rows="3"
            placeholder="Add a custom welcome message for your invitees..."
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            This message will appear above the group description
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="button_text">Join Button Text</label>
          <input
            type="text"
            id="button_text"
            name="button_text"
            value={formData.button_text}
            onChange={handleChange}
            placeholder="Join Group"
            maxLength="50"
          />
        </div>

        <div className="form-group">
          <label htmlFor="background_color">Background Color</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="color"
              id="background_color"
              name="background_color"
              value={formData.background_color}
              onChange={handleChange}
              style={{ width: '60px', height: '40px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            />
            <input
              type="text"
              value={formData.background_color}
              onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
              placeholder="#f8f9fa"
              style={{ flex: 1 }}
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save Customization'}
        </button>
      </form>

      <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #ddd' }}>
        <h4>Preview Link</h4>
        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
          Open this link to see how your join page looks:
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={previewLink}
            readOnly
            style={{ flex: 1, padding: '0.75rem', background: '#fff' }}
          />
          <button
            className="btn btn-secondary"
            onClick={() => window.open(previewLink, '_blank')}
          >
            Preview
          </button>
        </div>
      </div>
    </div>
  );
}


export default GroupDetail;
